import { useTranslation } from "react-i18next";
import {
  Shield,
  ShieldCheck,
  Briefcase,
  Mail,
  Handshake,
  Newspaper,
  FileText,
  ArrowRight,
} from "lucide-react";
import LocaleLink from "@/components/LocaleLink";

interface MegaMenuCompanyProps {
  onItemClick?: () => void;
}

interface Item {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  desc: string;
}

const MegaMenuCompany = ({ onItemClick }: MegaMenuCompanyProps) => {
  const { t } = useTranslation();

  const company: Item[] = [
    {
      to: "/about",
      icon: Shield,
      title: t("navigation.aboutCsquare"),
      desc: t("navigation.aboutCsquareDesc"),
    },
    {
      to: "/careers",
      icon: Briefcase,
      title: t("navigation.careers"),
      desc: t("navigation.careersDesc"),
    },
    {
      to: "/contact",
      icon: Mail,
      title: t("navigation.contactUs"),
      desc: t("navigation.contactUsDesc"),
    },
    {
      to: "/trust",
      icon: ShieldCheck,
      title: t("navigation.trustCenter"),
      desc: t(
        "navigation.trustCenterDesc"
      ),
    },
  ];

  const partnersPress: Item[] = [
    {
      to: "/partners",
      icon: Handshake,
      title: t("navigation.partners"),
      desc: t("navigation.partnersDesc"),
    },
    {
      to: "/newsroom",
      icon: Newspaper,
      title: t("navigation.newsroom"),
      desc: t(
        "navigation.newsroomDesc"
      ),
    },
    {
      to: "/press-kit",
      icon: FileText,
      title: t("navigation.pressKit"),
      desc: t("navigation.pressKitDesc"),
    },
  ];

  const renderItem = (item: Item) => {
    const Icon = item.icon;
    return (
      <LocaleLink
        key={item.to}
        to={item.to}
        onClick={onItemClick}
        className="flex items-start gap-3 p-3 hover:bg-secondary transition-colors group"
      >
        <Icon className="h-5 w-5 text-accent mt-0.5" />
        <div className="min-w-0">
          <div className="font-medium text-sm group-hover:text-primary">{item.title}</div>
          <div className="text-xs text-muted-foreground">{item.desc}</div>
        </div>
      </LocaleLink>
    );
  };

  return (
    <div className="w-[560px] p-6">
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.companyCol")}
          </h3>
          <div className="space-y-1">{company.map(renderItem)}</div>
        </div>
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.partnersPressCol")}
          </h3>
          <div className="space-y-1">{partnersPress.map(renderItem)}</div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          {t("megaMenu.wantToPartner")}
        </span>
        <LocaleLink
          to="/book-demo"
          onClick={onItemClick}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          {t("megaMenu.bookADemo")}
          <ArrowRight className="h-4 w-4" />
        </LocaleLink>
      </div>
    </div>
  );
};

export default MegaMenuCompany;
